import {
  type AccountStatus,
  type PaymentIssue,
  type PremiumFeatures,
} from '@/services/types/premiumFeatures/premiumFeatures'
import { START_THREATEN_DAY } from '@/utils/constants/premiumFeatures'
import { differenceInDays } from 'date-fns'

import { create } from 'zustand'

export interface PremiumFeaturesActions {
  setPremiumFeatures: (premiumFeatures: PremiumFeatures[]) => void
  setStatus: (status: AccountStatus) => void
  setPaymentIssue: (paymentIssue: PaymentIssue) => void
  setAccountCreatedAt: (createdAt: Date) => void
}

export interface PremiumFeaturesState {
  actions: PremiumFeaturesActions
  premiumFeatures: PremiumFeatures[]
  isLoading: boolean
  status: AccountStatus | null
  accountCreatedAt: Date | null
  paymentIssue?: {
    paymentAttempts: number
    lastAttempt: Date
    gracePeriod: Date
    shouldThreat: boolean
    shouldWarn: boolean
  }
}

const INITIAL_STATE: Omit<PremiumFeaturesState, 'actions'> = {
  premiumFeatures: [],
  isLoading: true,
  status: null,
  accountCreatedAt: null,
}

export const usePremiumFeaturesStore = create<PremiumFeaturesState>()((set) => ({
  ...INITIAL_STATE,
  actions: {
    setPremiumFeatures: (premiumFeatures: PremiumFeatures[]) => {
      set(() => ({ premiumFeatures, isLoading: false }))
    },
    setStatus: (status: AccountStatus) => {
      set(() => ({ status }))
    },
    setAccountCreatedAt: (createdAt: Date | null) => {
      set(() => ({ accountCreatedAt: createdAt }))
    },
    setPaymentIssue: (paymentIssue: PaymentIssue) => {
      set(() => {
        const daysUntilGracePeriod = differenceInDays(paymentIssue.gracePeriod, new Date())

        return {
          paymentIssue: {
            paymentAttempts: paymentIssue.paymentAttempts,
            lastAttempt: paymentIssue.lastAttempt,
            gracePeriod: paymentIssue.gracePeriod,
            shouldWarn: daysUntilGracePeriod > START_THREATEN_DAY,
            shouldThreat: daysUntilGracePeriod <= START_THREATEN_DAY,
          },
        }
      })
    },
  },
}))

export const usePremiumFeaturesPermissions = () =>
  usePremiumFeaturesStore((state) => state.premiumFeatures)
export const usePremiumFeaturesStatus = () => usePremiumFeaturesStore((state) => state.status)
export const usePremiumFeaturesPaymentIssue = () =>
  usePremiumFeaturesStore((state) => state.paymentIssue)
export const usePremiumFeaturesAccountCreatedAt = () =>
  usePremiumFeaturesStore((state) => state.accountCreatedAt)
